import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { motion, useAnimation } from "framer-motion";

const disableScroll = () => {
  if (typeof document === "undefined") return;
  document.body.style.overflow = "hidden";
};

const enableScroll = () => {
  if (typeof document === "undefined") return;
  document.body.style.overflow = "auto";
};

export default function ScrollDotsMain() {
  const sections = useMemo(() => ["hero", "games", "contact"], []);
  const total = sections.length;

  const dotSize = 8;
  const gap = 18;
  const travel = dotSize + gap;

  const [active, setActive] = useState(0);
  const [isDesktop, setIsDesktop] = useState(false); 
  const controls = useAnimation();

  const activeRef = useRef(0);
  const isAnimatingRef = useRef(false);
  const isReadyRef = useRef(false);
  const isMountedRef = useRef(false);

  const setActiveSafe = useCallback((i) => {
    setActive(i);
    activeRef.current = i;
  }, []);

  const safeStart = useCallback(
    (animation) => {
      if (!isMountedRef.current) return;
      return controls.start(animation);
    },
    [controls]
  );

  const safeSet = useCallback(
    (values) => {
      if (!isMountedRef.current) return;
      return controls.set(values);
    },
    [controls]
  );

  useEffect(() => {
    const checkViewport = () => {
      if (typeof window === "undefined") return;
      setIsDesktop(window.innerWidth >= 1024);
    };

    checkViewport();
    window.addEventListener("resize", checkViewport);
    return () => window.removeEventListener("resize", checkViewport);
  }, []);

  const triggerStretch = useCallback(
    async (next) => {
      if (!isDesktop) return;
      if (isAnimatingRef.current) await controls.stop();
      if (next === activeRef.current) return;

      isAnimatingRef.current = true;
      disableScroll();

      const current = activeRef.current;
      const distance = Math.abs(next - current);
      const goingDown = next > current;

      await safeStart({
        y: goingDown ? current * travel : next * travel,
        height: distance * travel + dotSize,
        originY: goingDown ? 0 : 1,
        transition: { duration: 0.18, ease: "easeInOut" },
      });

      setActiveSafe(next);

      await safeStart({
        y: next * travel,
        height: dotSize,
        transition: { type: "spring", stiffness: 400, damping: 18 },
      });

      setTimeout(() => {
        enableScroll();
        isAnimatingRef.current = false;
      }, 180);
    },
    [controls, safeStart, setActiveSafe, travel, isDesktop]
  );

  const scrollToSection = useCallback(
    (id) => {
      if (!isDesktop) return;
      const section = document.getElementById(id);
      if (!section) return;

      disableScroll();
      section.scrollIntoView({ behavior: "smooth", block: "start" });

      setTimeout(() => {
        const index = sections.indexOf(id);
        if (index >= 0) triggerStretch(index);
      }, 60);
    },
    [sections, triggerStretch, isDesktop]
  );

  useEffect(() => {
    isMountedRef.current = true;

    if (isDesktop) {
      window.scrollDotsTo = (id) => {
        if (!isReadyRef.current || isAnimatingRef.current) return;
        scrollToSection(id);
      };
    } else {
      window.scrollDotsTo = null;
    }

    return () => {
      isMountedRef.current = false;
      window.scrollDotsTo = null;
    };
  }, [scrollToSection, isDesktop]);

  useEffect(() => {
    if (!isDesktop) return;

    const handleWheel = (e) => {
      if (!isReadyRef.current || isAnimatingRef.current) return;
      e.preventDefault();

      const cur = activeRef.current;
      if (e.deltaY > 0 && cur < total - 1) {
        scrollToSection(sections[cur + 1]);
      } else if (e.deltaY < 0 && cur > 0) {
        scrollToSection(sections[cur - 1]);
      }
    };

    window.addEventListener("wheel", handleWheel, { passive: false });
    return () => window.removeEventListener("wheel", handleWheel);
  }, [sections, scrollToSection, total, isDesktop]);

  useEffect(() => {
    if (!isDesktop) return;

    const handleScroll = () => {
      if (!isReadyRef.current || isAnimatingRef.current) return;

      let current = 0;
      sections.forEach((id, i) => {
        const el = document.getElementById(id);
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (rect.top <= window.innerHeight * 0.5) current = i;
      });

      if (current !== activeRef.current) {
        setActiveSafe(current);
        safeStart({
          y: current * travel,
          height: dotSize,
          transition: { type: "spring", stiffness: 300, damping: 25 },
        });
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [sections, safeStart, travel, setActiveSafe, isDesktop]);

  useEffect(() => {
    if (!isDesktop) {
      enableScroll();
      isReadyRef.current = false;
      return;
    }

    disableScroll();
    const timeout = setTimeout(() => {
      enableScroll();
      isReadyRef.current = true;

      let current = 0;
      sections.forEach((id, i) => {
        const el = document.getElementById(id);
        if (!el) return;
        const rect = el.getBoundingClientRect();
        if (rect.top <= window.innerHeight * 0.5) current = i;
      });

      setActiveSafe(current);
      safeSet({ y: current * travel, height: dotSize });
    }, 350);

    return () => clearTimeout(timeout);
  }, [sections, safeSet, travel, setActiveSafe, isDesktop]);

  const handleClick = (i) => {
    if (!isDesktop) return;
    if (!isReadyRef.current || isAnimatingRef.current) return;
    scrollToSection(sections[i]);
  };

  if (!isDesktop) return null;

  return (
    <div className="fixed top-1/2 right-8 -translate-y-1/2 z-50">
      <div
        className="relative flex flex-col items-center"
        style={{ gap: `${gap}px` }}
      >
        {sections.map((_, i) => (
          <div
            key={i}
            onClick={() => handleClick(i)}
            className={`w-2 h-2 rounded-full cursor-pointer transition ${
              active === i
                ? "bg-indigo-400 scale-110"
                : "bg-gray-600 hover:bg-indigo-300"
            }`}
          />
        ))}

        <motion.div
          animate={controls}
          initial={{ y: active * travel, height: dotSize }}
          className="absolute w-2 rounded-full bg-indigo-500 shadow-[0_0_6px_2px_rgba(99,102,241,0.6)]"
        />
      </div>
    </div>
  );
}
